import * as batshit from "@yornaath/batshit";
import {HTTPError} from "platformed-client";
import {
  ConversationId,
  InternalConversationCounters,
  InternalConversationFilter,
  Region,
} from "platformed-client/types";
import api from "../../../api";
import {scheduler} from "../../batch";
import {param, typedQuery} from "../../typing";
import {decodeCounterKey, encodeCounterKey, groupCounterKeysByRegion} from "./counterKeys";

export const conversations = typedQuery(
  ["internal", "conversations", "list", param<Region[]>("regions"), param<InternalConversationFilter>("filter")],
  async (regions, filter) => {
    return await api.internal.conversations.listConversations(regions, filter);
  },
);

export const conversation = typedQuery(
  ["internal", "conversations", "detail", param<Region>("region"), param<ConversationId>("conversationId")],
  async (region, conversationId) => {
    return await api.internal.conversations.getConversation(region, conversationId);
  },
);

// Coalesces per-row counter refetches, used imperatively when a live
// `EventAppended` lands (not as a query). Key helpers live in `counterKeys`.
const countersBatcher = batshit.create({
  fetcher: async (keys: string[]) => {
    // allSettled, not all: a batch can span regions, so one unhealthy region
    // must not fail the refresh for rows in the healthy ones. A rejected
    // region contributes no rows; those keys 404 in the resolver and the
    // caller keeps the row's last-known values.
    const perRegion = await Promise.allSettled(
      [...groupCounterKeysByRegion(keys)].map(([region, ids]) =>
        api.internal.conversations.loadConversationCounters(region, ids),
      ),
    );
    return perRegion.flatMap(r => (r.status === "fulfilled" ? r.value : []));
  },
  resolver: (items: InternalConversationCounters[], key: string): InternalConversationCounters => {
    const {conversationId} = decodeCounterKey(key);
    const found = items.find(s => s.conversation_id === conversationId);
    // A key with no row — the conversation was removed between the event and
    // the refetch, or its region's request failed — rejects only that key's
    // promise (a 404); `refreshRow` catches it and leaves the row as-is.
    if (found == null) throw new HTTPError(new Response(null, {status: 404}));
    return found;
  },
  scheduler,
});

/** Fetch one conversation's volatile counters, coalescing concurrent calls. */
export function fetchConversationCounters(
  region: Region,
  conversationId: ConversationId,
): Promise<InternalConversationCounters> {
  return countersBatcher.fetch(encodeCounterKey(region, conversationId));
}
